import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import type { MapLayerMouseEvent } from 'react-map-gl';
import type { Position } from 'geojson';
import type { Pieces, TraceSource, TraceTrack } from './types';
import {
  buildTrack,
  closeTrace,
  nearestOnTrack,
  pointOnTrack,
  verticesAlong,
} from './utils';
import { traceStopper } from './trace-utils';
import { followDrag } from 'components/create-area-panel';
import { VERTEX_LAYER } from './vertex-edit-layer';

const SOURCE = 'area-edit-trace-preview';

/** How near (px) the pointer must come to a reference ring to follow it. */
const CAPTURE_PX = 24;

interface TraceGestureProps {
  pieces: Pieces;
  /** The boundaries the run can follow: shown areas, counties and states. */
  references: TraceSource[];
  onChange: (pieces: Pieces) => void;
  onBeforeChange: () => void;
}

/** A reference ring the trace can follow. */
interface Ring {
  key: string;
  track: TraceTrack;
}

/** A ring the pointer is near, and where on it. */
interface RingHit {
  ring: Ring;
  param: number;
  distance: number;
  position: Position;
}

/** Where on a ring the trace is, and how far it has run along it. */
interface Following {
  ring: Ring;
  startParam: number;
  entry: Position;
}

/**
 * Every ring of every boundary to follow, laid out for tracing.
 *
 * @param {TraceSource[]} sources - The boundaries.
 * @return {Ring[]} Their rings.
 */
const referenceRings = (sources: TraceSource[]): Ring[] =>
  sources.flatMap((source) => {
    const polygons =
      source.geometry.type === 'Polygon'
        ? [source.geometry.coordinates]
        : source.geometry.coordinates;
    return polygons.flatMap((rings, p) =>
      rings.map((ring, r) => ({
        key: `${source.key}:${p}:${r}`,
        track: buildTrack(ring),
      }))
    );
  });

/** The map projection the trace measures with. */
interface Projection {
  project: (position: [number, number]) => { x: number; y: number };
  unproject: (point: [number, number]) => { lng: number; lat: number };
}

/**
 * A screen distance in degrees at the current zoom, squared, as the track maths
 * measures.
 *
 * @param {Projection} map - The map.
 * @param {number} px - The distance, in px.
 * @return {number} The squared distance in degrees.
 */
const degreesSq = (map: Projection, px: number): number => {
  const a = map.unproject([0, 0]);
  const b = map.unproject([px, 0]);
  return (b.lng - a.lng) ** 2 + (b.lat - a.lat) ** 2;
};

/**
 * How far apart two map positions are on screen.
 *
 * @param {Projection} map - The map.
 * @param {Position} a - One position.
 * @param {Position} b - The other.
 * @return {number} The distance, in px.
 */
const pixelsApart = (map: Projection, a: Position, b: Position): number => {
  const pa = map.project(a as [number, number]);
  const pb = map.project(b as [number, number]);
  return Math.hypot(pa.x - pb.x, pa.y - pb.y);
};

/**
 * The ring nearest a point, within reach.
 *
 * @param {Ring[]} rings - The rings to follow.
 * @param {Position} point - The pointer.
 * @param {number} reach - The capture radius, squared, in degrees.
 * @return {RingHit | null} The ring and where on it.
 */
const nearestRingWithin = (
  rings: Ring[],
  point: Position,
  reach: number
): RingHit | null => {
  let best: RingHit | null = null;
  for (const ring of rings) {
    if (ring.track.total > 0) {
      const near = nearestOnTrack(ring.track, point);
      if (!best || near.distance < best.distance) best = { ...near, ring };
    }
  }
  return best && best.distance <= reach ? best : null;
};

/**
 * The run so far, previewed in blue.
 *
 * @param {{ line: Position[] }} props - The run.
 * @return {JSX.Element} The preview line.
 */
function TracePreview({ line: preview }: { line: Position[] }): JSX.Element {
  return (
    <Source
      id={SOURCE}
      type="geojson"
      data={{
        type: 'FeatureCollection',
        features:
          preview.length > 1
            ? [
                {
                  type: 'Feature',
                  properties: {},
                  geometry: { type: 'LineString', coordinates: preview },
                },
              ]
            : [],
      }}
    >
      <Layer
        id={SOURCE}
        type="line"
        paint={{
          'line-color': '#0f62fe',
          'line-width': 3,
          'line-opacity': 0.9,
        }}
      />
    </Source>
  );
}

/**
 * Trace shown boundary, as the mock has it: press on a vertex of the area, drag
 * along a shown reference ring, or a county or state line, and release. The run follows the cursor's
 * direction, hops to another ring once the cursor is clearly nearer it, previews
 * in blue, and Esc drops it. The run pauses at each corner of the ring it follows
 * and where it crosses the area's boundary, until the drag goes TRACE_STOP_PX
 * (40px) further, so a release near one lands exactly on it. On release the run
 * replaces the grabbed vertex and the part closes round its larger side, so the
 * shared edge is the reference's own vertices.
 *
 * @param {TraceGestureProps} props - The parts, the references and the commit.
 * @return {JSX.Element} The preview line.
 */
export function TraceGesture({
  pieces,
  references,
  onChange,
  onBeforeChange,
}: TraceGestureProps): JSX.Element {
  const { current: mapRef } = useMap();
  const [preview, setPreview] = useState<Position[]>([]);
  const rings = useMemo(() => referenceRings(references), [references]);
  const latest = useRef({ pieces, rings, onChange, onBeforeChange });
  useLayoutEffect(() => {
    latest.current = { pieces, rings, onChange, onBeforeChange };
  }, [pieces, rings, onChange, onBeforeChange]);

  useEffect(() => {
    if (!mapRef) return undefined;
    const map = mapRef.getMap();
    const canvas = map.getCanvas();
    canvas.style.cursor = 'crosshair';
    // Drops a trace still in progress if the gesture unmounts mid-drag, so its
    // window listeners don't outlive it and apply the run after the tool changed.
    let abortTrace: (() => void) | null = null;

    /**
     * The reference ring nearest a point, within reach.
     *
     * @param {Position} point - The pointer.
     * @return {object | null} The ring and where on it.
     */
    const nearestRing = (point: Position): RingHit | null =>
      nearestRingWithin(
        latest.current.rings,
        point,
        degreesSq(map, CAPTURE_PX)
      );

    /**
     * Starts a trace from a grabbed vertex.
     *
     * @param {MapLayerMouseEvent} event - The press.
     */
    const handleMouseDown = (event: MapLayerMouseEvent) => {
      if (!map.getLayer(VERTEX_LAYER)) return;
      const props = map.queryRenderedFeatures(event.point, {
        layers: [VERTEX_LAYER],
      })[0]?.properties as { p?: unknown; i?: unknown } | undefined;
      if (typeof props?.p !== 'number' || typeof props?.i !== 'number') return;
      const p = props.p;
      const i = props.i;
      event.preventDefault();
      map.dragPan.disable();
      canvas.style.cursor = 'grabbing';

      const grabbed = [...latest.current.pieces[p][i]];
      // Read and written by the handlers below, so held in one object rather than
      // let-bindings the closures would each narrow differently.
      const trace: {
        following: Following | null;
        /** How far the pointer has run along the ring; the stops may hold the trace back. */
        run: number;
        lastParam: number;
        pointer: Position;
      } = {
        following: null,
        run: 0,
        lastParam: 0,
        pointer: grabbed,
      };
      const stopper = traceStopper((a, b) => pixelsApart(map, a, b));
      let committed: Position[] = [grabbed];
      let current: Position[] = [grabbed];

      /**
       * The positions run along the ring being followed.
       *
       * @return {Position[]} The run so far on this ring.
       */
      const along = (): Position[] => {
        const { following } = trace;
        const run = stopper.shown();
        if (!following) return [];
        if (Math.abs(run) < 1e-9) return [following.entry];
        return [
          following.entry,
          ...verticesAlong(following.ring.track, following.startParam, run),
          pointOnTrack(following.ring.track, following.startParam + run),
        ];
      };

      /**
       * Starts following a ring where the pointer meets it.
       *
       * @param {object} hit - The ring and where on it.
       */
      const follow = (hit: RingHit) => {
        trace.following = {
          ring: hit.ring,
          startParam: hit.param,
          entry: [...hit.position],
        };
        trace.lastParam = hit.param;
        trace.run = 0;
        stopper.join(
          hit.ring.track,
          hit.param,
          hit.position,
          latest.current.pieces
        );
      };

      /**
       * Extends the run as the pointer moves along, or hops rings.
       *
       * @param {Position} point - Where the pointer is.
       */
      const handleMove = (point: Position) => {
        trace.pointer = point;
        const reach = degreesSq(map, CAPTURE_PX);
        const { following } = trace;
        if (following) {
          const { track } = following.ring;
          const onRing = nearestOnTrack(track, point);
          const other = nearestRing(point);
          if (
            other &&
            other.ring.key !== following.ring.key &&
            (other.distance * 4 < onRing.distance || onRing.distance > reach)
          ) {
            committed = [...committed, ...along()];
            follow(other);
          } else if (onRing.distance <= reach) {
            let step = onRing.param - trace.lastParam;
            if (step > track.total / 2) step -= track.total;
            if (step < -track.total / 2) step += track.total;
            trace.run = Math.max(
              -track.total * 0.98,
              Math.min(track.total * 0.98, trace.run + step)
            );
            trace.lastParam = onRing.param;
          }
        } else {
          const hit = nearestRing(point);
          if (!hit) {
            current = [...committed];
            setPreview([...committed, point]);
            return;
          }
          follow(hit);
        }
        if (trace.following) stopper.settle(trace.run, point);
        current = [...committed, ...along()];
        setPreview(current);
      };

      /**
       * Ends the trace, keeping it on release and dropping it on Esc.
       *
       * @param {boolean} keep - Whether to apply the run.
       */
      const finish = (keep: boolean) => {
        abortTrace = null;
        stopDrag();
        window.removeEventListener('keydown', handleKey, true);
        map.dragPan.enable();
        canvas.style.cursor = 'crosshair';
        setPreview([]);
        if (!keep || !trace.following) return;
        stopper.land(trace.pointer);
        current = [...committed, ...along()];
        if (current.length < 2) return;
        const next = closeTrace(latest.current.pieces[p], i, current);
        if (!next) return;
        latest.current.onBeforeChange();
        latest.current.onChange(
          latest.current.pieces.map((piece, index) =>
            index === p ? next : piece
          )
        );
      };
      /**
       * Drops the trace on Esc.
       *
       * @param {KeyboardEvent} key - The key press.
       */
      const handleKey = (key: KeyboardEvent) => {
        if (key.key !== 'Escape') return;
        // Caught first and stopped, so Esc drops the trace without closing the
        // editor around it.
        key.stopPropagation();
        finish(false);
      };

      // Followed on the window, so a release anywhere ends the trace and applies it.
      const stopDrag = followDrag(map, {
        move: handleMove,
        end: () => finish(true),
      });
      window.addEventListener('keydown', handleKey, true);
      abortTrace = () => finish(false);
    };

    /**
     * Grab over a vertex, crosshair elsewhere.
     *
     * @param {MapLayerMouseEvent} event - The move.
     */
    const handleHover = (event: MapLayerMouseEvent) => {
      if (canvas.style.cursor === 'grabbing' || !map.getLayer(VERTEX_LAYER))
        return;
      const over =
        map.queryRenderedFeatures(event.point, { layers: [VERTEX_LAYER] })
          .length > 0;
      canvas.style.cursor = over ? 'grab' : 'crosshair';
    };

    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleHover);
    return () => {
      abortTrace?.();
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleHover);
      map.dragPan.enable();
      canvas.style.cursor = '';
    };
  }, [mapRef]);

  return <TracePreview line={preview} />;
}
